import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { getEnvironmentConfigs } from '../../../environmentConfigs.ts';
import { useAuth } from '../components/AuthContext.tsx';
import { SsoLayout } from '../components/SsoLayout.tsx';
import { isWrongCredentialsMessage } from '../utils/isWrongCredentialsMessage.ts';
import { isOAuthQueryValid } from '../utils/verifyOAuthParams.ts';
import { EMAIL_FIELD, type FormValues, LoginForm, PASSWORD_FIELD, validationSchema } from './LoginForm.tsx';
import { PrivacyNotice } from './PrivacyNotice.tsx';

const initialValues: FormValues = {
  [EMAIL_FIELD]: '',
  [PASSWORD_FIELD]: '',
};

export const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, signInWithEmailAndPassword } = useAuth();
  const { unityOrigin } = getEnvironmentConfigs();

  const formMethods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = useCallback(
    async (values: FormValues): Promise<void> => {
      try {
        if (searchParams.has('login_hint')) {
          searchParams.delete('login_hint');
          setSearchParams(searchParams);
        }

        await signInWithEmailAndPassword(values[EMAIL_FIELD], values[PASSWORD_FIELD]);
      } catch (error) {
        // we display general error message for wrong password or email and others errors
        const message = isWrongCredentialsMessage(error.message)
          ? 'Invalid username or password'
          : 'Something was wrong with login process. Please try again later.';

        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    },
    [signInWithEmailAndPassword, enqueueSnackbar, searchParams, setSearchParams],
  );

  const onForgotPasswordHandler = useCallback(
    () => navigate(`/forgot-password${location.search}${location.hash}`),
    [location, navigate],
  );

  if (currentUser && !isOAuthQueryValid(location)) {
    if (unityOrigin) {
      window.location.href = unityOrigin;
    }
  }

  if (currentUser && isOAuthQueryValid(location)) {
    return <Navigate to={`/consent${location.search}${location.hash}`} replace />;
  }

  return (
    <SsoLayout>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <FormProvider {...formMethods}>
          <LoginForm onSubmitHandler={formMethods.handleSubmit(onSubmit)} onForgotPassword={onForgotPasswordHandler} />
        </FormProvider>

        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',

            '@media (max-height: 700px)': {
              position: 'static',
            },
          }}
        >
          <PrivacyNotice />
        </Box>
      </Box>
    </SsoLayout>
  );
};
